import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { useAppColors } from '../../hooks/useAppColors';
import { useTranslation } from 'react-i18next';
import { MEMBERSHIP_TERM_OPTIONS } from './billingUtils';

interface TermSelectorProps {
  selectedTermMonths: number;
  onSelectTerm: (termMonths: number) => void;
  compact?: boolean;
  /** Restrict which terms are shown. Defaults to all MEMBERSHIP_TERM_OPTIONS. */
  allowedTermMonths?: number[];
}

export default function TermSelector({
  selectedTermMonths,
  onSelectTerm,
  compact = false,
  allowedTermMonths,
}: TermSelectorProps) {
  const { t } = useTranslation();
  const colors = useAppColors();

  const options = allowedTermMonths
    ? MEMBERSHIP_TERM_OPTIONS.filter((o) => allowedTermMonths.includes(o.months))
    : MEMBERSHIP_TERM_OPTIONS;

  // If the current selection is not in the allowed set, auto-select the first available
  React.useEffect(() => {
    if (options.length > 0 && !options.some((o) => o.months === selectedTermMonths)) {
      onSelectTerm(options[0].months);
    }
  }, [options, selectedTermMonths, onSelectTerm]);

  return (
    <View style={[styles.termList, compact && styles.compact]}>
      {options.map((option) => {
        const selected = selectedTermMonths === option.months;
        return (
          <TouchableOpacity
            key={option.months}
            onPress={() => onSelectTerm(option.months)}
            style={[
              styles.termButton,
              {
                backgroundColor: selected ? colors.accent : colors.card,
                borderColor: selected ? colors.accent : colors.border,
              },
            ]}
            activeOpacity={0.7}
          >
            <Text style={[
              styles.termButtonText,
              { color: selected ? colors.accentText : colors.text },
            ]}>
              {option.months} {(option.months as number) === 1 ? t('common.month') : t('common.months')}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  termList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  compact: {
    gap: 6,
    marginBottom: 8,
  },
  termButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1.5,
    minWidth: 70,
    alignItems: 'center',
  },
  termButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
